import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200ClientFeatureFlagsItem,
  PostApiV1ThreeLetterCodePartnerAdminsClientsBody,
} from '@fukuri-admins/apis/client/gen/model';
import type { CreateClientFormFieldValues } from '../schema';

type Args = {
  formValues: CreateClientFormFieldValues;
  featureFlags: GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200ClientFeatureFlagsItem[];
};

type IGenerateRequestBody = (args: Args) => PostApiV1ThreeLetterCodePartnerAdminsClientsBody;

/**
 * 選択済みフィーチャーフラグを { feature_key: boolean } のレコードに変換する
 * - selectedFlags に含まれるキーは true、含まれないキーは false となる
 */
const convertToClientFeatureFlagsRecord = (
  selectedFlags: string[],
  featureFlags: GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200ClientFeatureFlagsItem[]
): Record<string, boolean> =>
  featureFlags.reduce(
    (acc, flag) => {
      acc[flag.feature_key] = selectedFlags.includes(flag.feature_key);
      return acc;
    },
    {} as Record<string, boolean>
  );

/**
 * 単一選択グループID（文字列 | undefined）を number[] に変換する
 * - 未選択（undefined / 空文字）-> []
 * - 数値文字列 -> [数値]
 */
const parseOptionalNumberList = (val: string | undefined): number[] => {
  if (!val) return [];
  const parsed = Number.parseInt(val, 10);
  return Number.isNaN(parsed) ? [] : [parsed];
};

/**
 * フォームの値から利用企業作成 API リクエストボディを生成する
 *
 * - prefectureCode が空文字の場合は null として送信（PEN-902。`Number('')` が `0` になり不正な
 *   `prefecture_id: 0` が送信されてしまう不具合の修正。`openapi.yml` の POST リクエストボディ側でも
 *   `prefecture_id` は `nullable: true` と明記されている）
 * - partnerBranchId が空文字の場合は null として送信
 * - desired_start_month: 仕様変更（PEN-893）により利用開始日フィールドをフォームから削除したため送信しない
 * - groupId（単一選択文字列 | undefined）は parseOptionalNumberList で number[] に変換
 * - corporate_number: Zod が `.optional()` で未入力時は空文字が来る。
 *   API に空文字を送ると pattern（13桁）バリデーション違反になるため `|| undefined` で省略する
 * - external_client_id: Zod が `.optional()` だが API 側に pattern / minLength 制約なし。空文字をそのまま送っても問題ない
 * - nullable フィールド（name_kana 等）: Zod が `min(1)` の必須フィールドのため空文字は来ない。そのまま渡す
 * - contact_person_phone: Zod が `min(1)` の必須フィールドのため空文字は来ない。そのまま渡す（PEN-891）
 * - contact_preferred_time: Zod が `.optional()` で未入力時は空文字が来る。空文字は「未設定」を表すため
 *   `|| null` で null に変換して送信する（ユーザー指示、PEN-891）。
 *   NOTE: `openapi.yml` の POST リクエストボディ側の `contact_preferred_time` には `nullable: true` の
 *   明記がなく、Orval 生成型も `contact_preferred_time?: string`（`| null` 無し）になっている
 *   （`name_kana` 等、他の多くの任意フィールドには `nullable: true` が明記されているのと対照的）。
 *   一方でレスポンス側スキーマ（`private_client.contact_preferred_time`）は
 *   `nullable: true`・「未設定の場合null」と明記されているため、DB カラム自体は null 許容と推測され、
 *   実運用で null 送信が拒否される可能性は低いと判断した。ただし request スキーマ上は未保証のため、
 *   バックエンド側で 422 等になった場合はこのコメントを参照し `openapi.yml` の `nullable` 追記を検討すること
 * - gift_plan_id: API スキーマ上 number | null の必須フィールドのため削除できない。
 *   Client は ContractPlan を直接持たない（Partner - ContractPlan - ClientContract - Client の関係）ため、
 *   UI から契約プラン項目を削除した暫定対応として null を固定送信する。
 *   API 側で gift_plan_id が optional 化 / 廃止されたら本実装も削除すること。
 *
 * @param formValues - フォームの入力値
 * @param featureFlags - 利用可能なクライアント機能フラグ一覧
 */
export const generateRequestBody: IGenerateRequestBody = ({ formValues, featureFlags }) => {
  return {
    client: {
      name: formValues.name,
      name_kana: formValues.nameKana,
      representative_name: formValues.representativeName,
      // Zod が `.optional()` で未入力時は空文字が来る。空文字を API に送ると corporate_number は pattern 違反になるため省略する
      corporate_number: formValues.corporateNumber || undefined,
      external_client_id: formValues.externalClientId,
      group_ids: parseOptionalNumberList(formValues.groupId),
      postal_code: formValues.postalCode,
      prefecture_id: formValues.prefectureCode !== '' ? Number(formValues.prefectureCode) : null,
      city: formValues.city,
      street_address: formValues.streetAddress,
      employee_count: formValues.employeeCount,
      partner_branch_id:
        formValues.partnerBranchId !== '' ? Number(formValues.partnerBranchId) : null,
      // UI から契約プラン項目を削除した暫定対応。API 側で gift_plan_id が optional 化 / 廃止されたら削除すること。
      gift_plan_id: null,
      client_feature_flags: convertToClientFeatureFlagsRecord(
        formValues.clientFeatureFlags ?? [],
        featureFlags
      ),
      contact_person_name: formValues.contactPersonName,
      contact_person_name_kana: formValues.contactPersonNameKana,
      contact_person_email: formValues.contactPersonEmail,
      contact_person_phone: formValues.contactPersonPhone,
      // 空文字は「未設定」を表すため null に変換して送信する（ユーザー指示、PEN-891。詳細は関数 JSDoc 参照）
      contact_preferred_time: formValues.contactPreferredTime || null,
      billing_contact_name: formValues.billingContactName,
      billing_contact_name_kana: formValues.billingContactNameKana,
      billing_email: formValues.billingEmail,
      custom_field_values: [],
    },
  };
};
